import {users as usersShared} from '@cl/shared';
import {KnowledgeRagTopic} from 'common/src/modules/ai/ai.enums';
import {CampaignStep} from 'common/src/modules/campaignWorkflow/campaignWorkflow.enums';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {AgentName, EntityTypeToRename} from 'common/src/modules/shared/copilot/copilot.enums';
import {Agent} from 'common/src/modules/shared/copilot/copilot.types';
import analyzeExcelHeadersTool from 'common/src/modules/shared/copilot/tools/analyzeExcelHeadersTool/analyzeExcelHeadersTool';
import batchRenameEntitiesTool from 'common/src/modules/shared/copilot/tools/batchRenameEntitiesTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import agent from 'common/src/modules/shared/copilot/utils/creators/agent';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {CLStore} from 'common/src/redux/store';
import campaignContext from '../../contexts/campaignContext';
import campaignsLibraryContext from '../../contexts/campaignsLibraryContext';
import {CampaignsAgentsToolsNames} from '../../copilotCampaigns.enums';
import createCampaignTool from '../../tools/createCampaignTool';
import getCampaignsMetadataTool from '../../tools/getCampaignsMetadataTool';
import navigateToCampaignVersionTool from '../../tools/navigateToCampaignVersionTool';
import navigateToCampaignsLibraryTool from '../../tools/navigateToCampaignsLibraryTool';
import campaignSetupContext, {placementsSummaryContext} from './campaignSetupAgent.context';
import campaignSetupAgentInstructions from './campaignSetupAgent.instructions';
import addOrUpdateConditionCardTool from './tools/conditions/addOrUpdateConditionCardTool';
import addOrUpdateContentCardTool from './tools/contentFeeds/addOrUpdateContentCardTool';
import addOrUpdateCreativeCardTool from './tools/creatives/addOrUpdateCreativeCardTool';
import askAboutObjectsTool from './tools/general/askAboutObjectsTool/askAboutObjectsTool';
import autoArrangeCardsTool from './tools/general/autoArrangeCardsTool/autoArrangeCardsTool';
import cardsConnectionTool from './tools/general/cardsConnectionTool/cardsConnectionTool';
import deleteCardTool from './tools/general/deleteCardTool/deleteCardTool';
import getCardDetailsTool from './tools/general/getCardDetailsTool/getCardDetailsTool';
import readValuesFromExcelTool from './tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import verifyCampaignPathTool from './tools/general/verifyCampaignPathTool/verifyCampaignPathTool';
import addOrUpdateLocationCardTool from './tools/location/addOrUpdateLocationCardTool';
import addOrUpdateOptimizationCardTool from './tools/optimization/addOrUpdateOptimizationCardTool';
import addOrUpdateProductCardTool from './tools/products/addOrUpdateProductCardTool';
import addOrUpdateScheduleCardTool from './tools/schedule/addOrUpdateScheduleCardTool';
import addOrUpdateScheduleCardToolV2 from './standaloneAgents/promptToCardAgentV2/tools/schedule/addOrUpdateScheduleCardToolV2';
import addOrUpdateSequenceCardTool from './tools/sequence/addOrUpdateSequenceCardTool';
import getSelectionStrategiesTool from './tools/strategy/getSelectionStrategiesTool';
import addOrUpdateWeightedCardTool from './tools/weighted/addOrUpdateWeightedCardTool';
import addOrUpdateWeightedCardToolV2 from './standaloneAgents/promptToCardAgentV2/tools/weighted/addOrUpdateWeightedCardToolV2';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import addOrUpdateConditionCardToolNG from './standaloneAgents/promptToCardAgentV2/tools/conditions/addOrUpdateConditionCardToolV2';
import getSetupAgentModel from './utils/getSetupAgentModel';
import addOrUpdateDistributionCardTool from './tools/distribution/addOrUpdateDistributionCardTool';
import addOrUpdateOptimizationCardToolV2 from './standaloneAgents/promptToCardAgentV2/tools/optimization/addOrUpdateOptimizationCardToolV2';

function campaignSetupAgent(store: CLStore): Agent {
  const userHasCampaignsWriteRole = doesUserHaveRole(
    usersShared.userSubject.data,
    RoleTypeName.CampaignsWrite,
  );

  const role = 'You are the agent responsible for campaign logic/behavior setup.';

  const inScope = [
    ...(userHasCampaignsWriteRole ? ['Create campaign', 'Bulk rename campaigns'] : []),
    `Answer questions about campaigns (using the ${CampaignsAgentsToolsNames.GetCampaignsMetadata} tool)`,
    'Create/Define campaign logic and behaviour',
    'Help users create, update and delete cards',
    'Help users connect and disconnect between cards (including fallback creatives)',
    'Answer questions about campaign map cards',
    'Use Excel data to configure individual cards',
  ];

  const permissionsRelatedTools = userHasCampaignsWriteRole
    ? [
        createCampaignTool({isAllowingSocialCampaigns: true, store}),
        batchRenameEntitiesTool({
          entityType: EntityTypeToRename.Campaign,
          toolName: CampaignsAgentsToolsNames.BatchRenameCampaigns,
        }),
      ]
    : [];

  const useNewStandaloneAgent = isFlagEnabled(FeatureFlagKey.EnableNewSetupAgentStandaloneAgent);

  const tools = [
    //Campaigns tools
    ...permissionsRelatedTools,
    navigateToCampaignVersionTool(store),
    navigateToCampaignsLibraryTool(store),
    getCampaignsMetadataTool(store),

    //Cards tools
    ...(useNewStandaloneAgent
      ? [addOrUpdateWeightedCardToolV2(store)]
      : [addOrUpdateWeightedCardTool(store)]),
    addOrUpdateDistributionCardTool(store),
    ...(useNewStandaloneAgent
      ? [addOrUpdateOptimizationCardToolV2(store)]
      : [addOrUpdateOptimizationCardTool(store)]),
    addOrUpdateCreativeCardTool(store),
    ...(useNewStandaloneAgent
      ? [addOrUpdateScheduleCardToolV2(store)]
      : [addOrUpdateScheduleCardTool(store)]),
    ...(useNewStandaloneAgent
      ? [addOrUpdateConditionCardToolNG(store)]
      : [addOrUpdateConditionCardTool(store)]),
    addOrUpdateSequenceCardTool(store),
    addOrUpdateContentCardTool(store),
    addOrUpdateLocationCardTool(store),
    addOrUpdateProductCardTool(store),
    autoArrangeCardsTool(store),
    deleteCardTool(store),
    getCardDetailsTool(store),
    cardsConnectionTool(store),
    getSelectionStrategiesTool(store),
    //searchObjectsTool(store), Replaced with askAboutObjectsTool
    askAboutObjectsTool(store),

    //General tools
    getFilesTool(),
    analyzeExcelHeadersTool(),
    readValuesFromExcelTool(),
    verifyCampaignPathTool(CampaignStep.CampaignLogic),
  ];

  const contexts = [
    campaignContext(store),
    campaignSetupContext(store),
    campaignsLibraryContext(store),
    placementsSummaryContext(store),
  ];

  const instructions = campaignSetupAgentInstructions;

  return agent({
    name: AgentName.CampaignSetup,
    role,
    store,
    instructions,
    knowledgeTopics: [KnowledgeRagTopic.CampaignSetupAgent],
    tools,
    inScope,
    contexts,
    model: getSetupAgentModel(),
  });
}

export default campaignSetupAgent;
